import { GridSortModel } from "@mui/x-data-grid-pro";
import { useState, useEffect, useCallback } from "react";
import {
  useCreateStudyMutation,
  useDeleteStudyMutation,
  useGetAllStudiesMutation,
  useLazyStudyDetailsQuery,
  useUpdateStudyMutation,
} from "../store/services/studiesQuery";
import { debounce } from "lodash";
import { initialPaginationModelValues } from "../constants/paginationConstants";
import { useSnackbar } from "./useSnackbar";
import { AppState, useAppSelector } from "../store";
import { useLocation } from "react-router-dom";

export const useStudies = () => {
  const location = useLocation();
  const { handleError } = useSnackbar();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "study_id", sort: "asc" },
  ]);
  const [rows, setRows] = useState([]);
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModelValues
  );
  const [filterObj, setFilterObject] = useState("");

  const openAddStudyModal = useAppSelector(
    (state: AppState) => state.studiesSlice.openAddStudyModal
  );
  const openDeleteStudyDialog = useAppSelector(
    (state: AppState) => state.studiesSlice.openDeleteStudyDialog
  );
  const [
    createStudy,
    {
      data: createStudyData,
      isLoading: createStudyIsLoading,
      isError: createStudyIsError,
      error: createStudyError,
      isSuccess: createStudyIsSuccess,
    },
  ] = useCreateStudyMutation();
  const [
    getAllStudies,
    {
      data: allStudiesData,
      isLoading: allStudiesIsLoading,
      isError: allStudiesIsError,
      error: allStudiesError,
      isSuccess: allStudiesIsSuccess,
    },
  ] = useGetAllStudiesMutation();
  const [
    updateStudy,
    {
      data: updateStudyData,
      isLoading: updateStudyIsLoading,
      isError: updateStudyIsError,
      error: updateStudyError,
      isSuccess: updateStudyIsSuccess,
    },
  ] = useUpdateStudyMutation();
  const [
    deleteStudy,
    {
      data: deleteStudyData,
      isLoading: deleteStudyIsLoading,
      isError: deleteStudyIsError,
      error: deleteStudyError,
      isSuccess: deleteStudyIsSuccess,
    },
  ] = useDeleteStudyMutation();
  const [
    getStudyDetails,
    {
      data: studyDetailsData,
      isLoading: studyDetailsIsLoading,
      isError: studyDetailsIsError,
      error: studyDetailsError,
      isSuccess: studyDetailsIsSuccess,
    },
  ] = useLazyStudyDetailsQuery();

  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(model);
    }, 300),
    []
  );

  useEffect(() => {
    getAllStudies({ is_audit: true });
  }, []);

  const getAllStudieswithProps = (
    from: number,
    size: number,
    filterObj?: any
  ) => {
    getAllStudies({
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data: filterObj && filterObj?.advanceFilter,
    });
  };

  useEffect(() => {
    if (
      paginationModel?.pageSize &&
      !openAddStudyModal &&
      !openDeleteStudyDialog &&
      location.pathname == "/studies"
    ) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getAllStudieswithProps(0, pageSize, filterObj);
      } else {
        getAllStudieswithProps(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, filterObj, openAddStudyModal, openDeleteStudyDialog]);

  useEffect(() => {
    if (!allStudiesIsLoading && allStudiesIsSuccess) {
      setRowsCount(allStudiesData.count);
      const data = allStudiesData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele?.study_id,
          enrollment: "",
        };
      });
      setRows(allStudiesData.count > 0 ? data : []);
    }
  }, [allStudiesData, allStudiesIsSuccess]);

  useEffect(() => {
    if (allStudiesIsError) {
      const error: any = allStudiesError;
      handleError(error);
      setRows([]);
    }
  }, [allStudiesError]);

  return {
    createStudy,
    createStudyData,
    createStudyIsLoading,
    createStudyIsError,
    createStudyError,
    createStudyIsSuccess,
    getAllStudies,
    allStudiesData,
    allStudiesIsLoading,
    allStudiesIsError,
    allStudiesError,
    allStudiesIsSuccess,
    updateStudy,
    updateStudyData,
    updateStudyIsLoading,
    updateStudyIsError,
    updateStudyError,
    updateStudyIsSuccess,
    deleteStudy,
    deleteStudyData,
    deleteStudyIsLoading,
    deleteStudyIsError,
    deleteStudyError,
    deleteStudyIsSuccess,
    getStudyDetails,
    studyDetailsData,
    studyDetailsIsLoading,
    studyDetailsIsError,
    studyDetailsError,
    studyDetailsIsSuccess,
    rows,
    rowsCount,
    sortModel,
    setFilterObject,
    paginationModel,
    setPaginationModel,
    handleSortModelChange,
    getAllStudieswithProps,
  };
};
